// Where the firm's service account works: the Shared Drive it was
// invited to and the master case workbook. Lives in the gear on the head
// of the service-account block - the two ids are shared by all three of
// its rows, so they belong to the block, not to one row.
//
// Unlike the key blocks these are not secrets: they come back in full and
// the form edits them.

import { useState } from "react";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import {
  useGoogleWorkspaceConfig,
  useSaveGoogleWorkspaceConfig,
  useRemoveGoogleWorkspaceConfig,
  useProbeDriveMembership,
} from "../../hooks/useGoogleWorkspaceConfig";
import type { DriveMembershipProbe, GoogleWorkspaceConfigStatus } from "../../api/integrations";

type Field = "sharedDriveId" | "excelMasterFileId";

function sourceLine(s: GoogleWorkspaceConfigStatus): string {
  if (s.source === "db") return "Saved in Settings.";
  if (s.source === "env") return "From the server environment.";
  return "Nothing on file yet.";
}

function membershipLine(m: DriveMembershipProbe): string {
  // The same words the Drive row uses, so the two readings agree.
  const when = `checked ${m.checkedAt.slice(0, 16).replace("T", " ")} UTC`;
  if (m.ok) {
    const name = m.driveName ? `"${m.driveName}"` : "the Shared Drive";
    return `${m.roleLabel} of ${name} - ${when}.`;
  }
  return `${m.reason ?? `Drive answered ${m.errorCode}`} - ${when}.`;
}

export function GoogleWorkspaceSection() {
  // Mounted from the admin-only gear, but the flag stands on its own:
  // wherever this section lands, a non-admin never issues the GET.
  const user = useCurrentUser();
  const configQ = useGoogleWorkspaceConfig(isAdminRole(user?.role));
  const save = useSaveGoogleWorkspaceConfig();
  const remove = useRemoveGoogleWorkspaceConfig();
  const probe = useProbeDriveMembership();
  const [edits, setEdits] = useState<Partial<Record<Field, string>>>({});

  const status = configQ.data;
  // The verdict just asked for outranks the remembered one - while it is
  // about the id on file; the server holds the same line for its own.
  const fresh =
    probe.data && probe.data.driveId === (status?.sharedDriveId ?? "") ? probe.data : null;
  const membership = fresh ?? status?.membership ?? null;
  const stored = (f: Field) => status?.[f] ?? "";
  const value = (f: Field) => edits[f] ?? stored(f);
  const dirty = (["sharedDriveId", "excelMasterFileId"] as Field[]).some(
    (f) => value(f).trim() !== stored(f),
  );
  // The check answers about the SAVED id, so an unsaved edit makes it moot.
  const driveDirty = value("sharedDriveId").trim() !== stored("sharedDriveId");

  function onSave() {
    // Both ids go up, including the one nobody touched: the server row
    // wins whole, so saving one alone would leave the other reading null
    // and turn its surface off.
    save.mutate(
      {
        sharedDriveId: value("sharedDriveId").trim(),
        excelMasterFileId: value("excelMasterFileId").trim(),
      },
      { onSuccess: () => setEdits({}) },
    );
  }

  function onRemove() {
    const ok = window.confirm(
      "Remove the stored ids? Drive sync and the case sync stop until new ids are saved; " +
        "ids from the server environment, if any, apply again.",
    );
    if (!ok) return;
    remove.mutate(undefined, { onSuccess: () => setEdits({}) });
  }

  return (
    <section className="settings-section">
      <p className="settings-sub">
        Which Shared Drive the firm's service account reads, and which workbook the case sync
        imports. Ids, not credentials - the key itself stays on the server.
      </p>

      {configQ.isLoading && (
        <SkeletonGroup>
          <Skeleton width={220} />
          <Skeleton width={180} />
        </SkeletonGroup>
      )}
      {configQ.isError && (
        <p className="settings-error">Could not load the Workspace settings. Refresh to retry.</p>
      )}

      {status && (
        <>
          <p className="settings-sub" role="status">
            {sourceLine(status)}
          </p>
          {status.serviceAccountEmail ? (
            <p className="settings-hint">
              Invite {status.serviceAccountEmail} to the Shared Drive, then paste the ids it can
              reach.
            </p>
          ) : (
            <p className="settings-warn">
              No service-account key on the server - these ids stay unused until one is set.
            </p>
          )}
          {status.source === "db" && status.updatedAt && (
            <p className="settings-hint">
              Saved {new Date(status.updatedAt).toLocaleDateString()}
              {status.updatedByEmail ? ` by ${status.updatedByEmail}` : ""}
            </p>
          )}
        </>
      )}

      <div className="settings-grid">
        <label className="settings-field">
          <span className="settings-label">Shared Drive id</span>
          <input
            className="cc-field"
            type="text"
            spellCheck={false}
            placeholder="0A..."
            value={value("sharedDriveId")}
            onChange={(e) => setEdits((p) => ({ ...p, sharedDriveId: e.target.value }))}
          />
          <span className="settings-hint">The id in the Drive URL, after /drive/folders/.</span>
          {status && !status.sharedDriveId && (
            <span className="settings-warn">
              No Shared Drive id - the service account has nowhere to look.
            </span>
          )}
        </label>
        <label className="settings-field">
          <span className="settings-label">Master workbook id</span>
          <input
            className="cc-field"
            type="text"
            spellCheck={false}
            placeholder="1..."
            value={value("excelMasterFileId")}
            onChange={(e) => setEdits((p) => ({ ...p, excelMasterFileId: e.target.value }))}
          />
          <span className="settings-hint">
            The id in the Sheets URL, between /spreadsheets/d/ and /edit.
          </span>
          {status && !status.excelMasterFileId && (
            <span className="settings-warn">
              No master workbook id - the case sync has nothing to read.
            </span>
          )}
        </label>
      </div>

      <div className="settings-actions">
        <button
          type="button"
          className="settings-save-btn"
          // A save carries BOTH ids, so a state we could not read is a
          // state we must not overwrite - one blind save would erase the
          // id the admin never saw.
          disabled={!status || !dirty || save.isPending}
          onClick={onSave}
        >
          {save.isPending ? "Saving..." : "Save ids"}
        </button>
        {status?.source === "db" && (
          <button
            type="button"
            className="cc-link-btn danger"
            disabled={remove.isPending}
            onClick={onRemove}
          >
            Remove ids
          </button>
        )}
        {status?.sharedDriveId && status.serviceAccountEmail && (
          <button
            type="button"
            className="cc-link-btn"
            disabled={probe.isPending || driveDirty}
            title={
              driveDirty
                ? "Save the Shared Drive id first - the check is about the saved id."
                : undefined
            }
            onClick={() => probe.mutate()}
          >
            {probe.isPending ? "Checking..." : "Check access"}
          </button>
        )}
      </div>

      {save.isError && <p className="settings-error">{(save.error as Error).message}</p>}
      {remove.isError && <p className="settings-error">{(remove.error as Error).message}</p>}
      {save.isSuccess && !dirty && (
        <p className="settings-ok" role="status">
          Ids saved.
        </p>
      )}
      {membership?.ok && (
        <p className="settings-ok" role="status">
          {membershipLine(membership)}
        </p>
      )}
      {membership && !membership.ok && (
        <p className="settings-error">{membershipLine(membership)}</p>
      )}
      {probe.isError && <p className="settings-error">{(probe.error as Error).message}</p>}
    </section>
  );
}
